import { useListingStepNavigation } from '@/lib/hooks/use-listing-step-navigation'
/**
 * New Listing — Step 1: Vehicle Details
 *
 * Collects make, model, year, category, color, transmission, fuel type,
 * seats, doors, and plate number.
 */
import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { View, Text, Keyboard, ScrollView } from 'react-native'
import { notification, NotificationType } from '@/lib/utils/haptics'
import { useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Animated from 'react-native-reanimated'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/TextInput'
import { Select, type SelectOption } from '@/components/ui/Select'
import { Chip } from '@/components/ui/Chip'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { FormActionBar } from '@/components/ui/FormActionBar'
import { Camera, ShieldCheck } from '@/components/ui/icons'
import { useListingWizardStore } from '@/lib/stores/listing-wizard.store'
import { useMakes, useModels } from '@/lib/hooks/use-host-vehicles'
import { useHostPublishReadiness } from '@/lib/hooks/use-host-publish-gate'
import { MIN_LISTING_PHOTOS } from '@/lib/types/listing-photo.types'
import { MIN_VEHICLE_YEAR, vehicleDetailsSchema } from '@/lib/validations/vehicle.schema'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { COLORS, FONTS } from '@/lib/brand'
import { normalizePlate, titleCase } from '@/lib/utils/sanitize'
import { MARKET_TRANSMISSIONS, toMarketTransmission } from '@/lib/utils/transmission'
import type { VehicleCategory, TransmissionType, FuelType } from '@/lib/types/vehicle.types'
import { useEntrance } from '@/lib/hooks/use-entrance'

// ─── Constants ──────────────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear()

const CATEGORIES: VehicleCategory[] = [
  'sedan',
  'suv',
  'pickup',
  'van',
  'compact',
  'luxury',
  'sports',
  'motorcycle',
  'microbus',
  'heavy_duty',
  'recreational',
]

const FUEL_TYPES: FuelType[] = ['gasoline', 'diesel', 'electric', 'hybrid', 'plugin_hybrid']

/**
 * Only years the schema ACCEPTS. This listed 20 years back (2006) while
 * vehicleDetailsSchema rejects anything under MIN_VEHICLE_YEAR, so a host with an
 * ordinary 2008 pickup picked a year the app itself offered and then could not
 * leave step 1 — the app disagreeing with itself, which reads as "this thing is
 * broken". Bounded by the shared constant so the two can never drift apart.
 */
function buildYearOptions(): SelectOption[] {
  const options: SelectOption[] = []
  for (let y = CURRENT_YEAR + 1; y >= MIN_VEHICLE_YEAR; y--) {
    options.push({ value: String(y), label: String(y) })
  }
  return options
}

// ─── Screen ─────────────────────────────────────────────────────────────────

export default function NewListingDetailsScreen() {
  const enter = useEntrance()
  const { t } = useTranslation()
  const { continueEditing, continueLabel } = useListingStepNavigation('ownership')
  const { vehicleId: searchVehicleId } = useLocalSearchParams<{ vehicleId?: string }>()
  const insets = useSafeAreaInsets()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  // No KYC/AML wall here anymore. A host builds the whole listing first; the
  // verification gate now lives at PUBLISH (review.tsx) so nobody is ejected to
  // /profile/verification before they see a single field. The finished listing
  // is saved as a draft and only publishing requires verification.

  // Wizard store
  const details = useListingWizardStore((s) => s.details)
  const setDetails = useListingWizardStore((s) => s.setDetails)
  const features = useListingWizardStore((s) => s.features)
  const setFeatures = useListingWizardStore((s) => s.setFeatures)
  const entryMode = useListingWizardStore((s) => s.entryMode)
  const isEditMode = !!searchVehicleId || entryMode === 'edit'
  const publishReadiness = useHostPublishReadiness()
  const willNeedVerification = !isEditMode && publishReadiness.resolved && !publishReadiness.passed

  // Lookups
  const { data: makes = [] } = useMakes()
  const { data: models = [] } = useModels(details.makeId)

  // Local validation errors
  const [errors, setErrors] = useState<Record<string, string>>({})
  // Validation errors render ABOVE the fold; without this the host taps
  // Siguiente, nothing visibly happens, and the step looks broken.
  const errorScrollRef = useRef<ScrollView>(null)
  const [colorText, setColorText] = useState(details.color ?? '')

  useEffect(() => {
    setColorText(details.color ?? '')
  }, [details.color])

  // Computed options
  const yearOptions = useMemo(() => buildYearOptions(), [])

  const makeOptions: SelectOption[] = useMemo(
    () => makes.map((m) => ({ value: m.id, label: m.name })),
    [makes],
  )

  const modelOptions: SelectOption[] = useMemo(
    () => models.map((m) => ({ value: m.id, label: m.name })),
    [models],
  )

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleMakeChange = useCallback(
    (makeId: string) => {
      setDetails({ makeId, modelId: undefined })
      setFeatures({ features: [] })
      setErrors((e) => ({ ...e, makeId: '', modelId: '' }))
    },
    [setDetails, setFeatures],
  )

  const handleModelChange = useCallback(
    (modelId: string) => {
      const model = models.find((m) => m.id === modelId)
      if (model) {
        // Auto-populate all known defaults from the model metadata
        setDetails({
          modelId,
          category: model.category,
          seats: model.defaultSeats,
          doors: model.defaultDoors,
          transmission:
            toMarketTransmission(model.defaultTransmission) ?? model.defaultTransmission,
          fuelType: model.defaultFuelType,
        })
        // Pre-fill known equipment per model, preserving any the host already
        // picked (e.g. after switching models). He can untick any of these.
        setFeatures({
          features: Array.from(new Set([...(features.features ?? []), ...model.defaultFeatures])),
        })
      } else {
        setDetails({ modelId })
      }
      setErrors((e) => ({ ...e, modelId: '' }))
    },
    [setDetails, setFeatures, models, features.features],
  )

  const handleYearChange = useCallback(
    (yearStr: string) => {
      setDetails({ year: parseInt(yearStr, 10) })
      setErrors((e) => ({ ...e, year: '' }))
    },
    [setDetails],
  )

  const handleNext = useCallback(() => {
    const normalizedColor = colorText.trim() ? titleCase(colorText.trim()) : colorText.trim()
    const normalizedDetails = {
      ...details,
      color: normalizedColor || undefined,
    }

    const result = vehicleDetailsSchema.safeParse({
      ...normalizedDetails,
      seats: details.seats ?? 5,
      doors: details.doors ?? 4,
      plateCountry: details.plateCountry ?? 'SV',
    })

    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        const field = issue.path[0] as string
        fieldErrors[field] = t(issue.message)
      }
      setErrors(fieldErrors)
      requestAnimationFrame(() => errorScrollRef.current?.scrollTo({ y: 0, animated: true }))
      void notification(NotificationType.Error)
      return
    }

    Keyboard.dismiss()
    setDetails(result.data)
    continueEditing()
  }, [colorText, details, setDetails, continueEditing, t])

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <ScreenContainer variant="content" scroll={false}>
      <KeyboardAwareView
        includeTopInset={false}
        scrollRef={errorScrollRef}
        contentContainerStyle={{
          padding: SPACING.md,
          paddingBottom: insets.bottom + SPACING['3xl'] + SPACING.xl,
        }}
      >
        {/* Requirements belong to the first step's content, not the custom
            navigation header. The header must keep one measured height across
            all eight routes or Android leaves the old banner height behind as
            a large blank strip on Fotos and every later step. */}
        {!isEditMode ? (
          <Animated.View
            collapsable={false}
            entering={enter(0)}
            style={{ marginBottom: SPACING.md }}
          >
            <View
              testID="listing.photo-requirement-notice"
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: SPACING.sm,
                paddingVertical: SPACING.sm,
                paddingHorizontal: SPACING.md,
                borderRadius: RADIUS.md,
                backgroundColor: `${COLORS.coral.DEFAULT}12`,
              }}
            >
              <Camera size={16} color={COLORS.coral.DEFAULT} strokeWidth={2} />
              <Text
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 12,
                  lineHeight: 17,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                }}
              >
                {t('listing.photos_upfront_notice', { total: MIN_LISTING_PHOTOS })}
              </Text>
            </View>

            {willNeedVerification ? (
              <View
                testID="listing.verification-upfront-notice"
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: SPACING.sm,
                  marginTop: SPACING.sm,
                  paddingVertical: SPACING.sm,
                  paddingHorizontal: SPACING.md,
                  borderRadius: RADIUS.md,
                  backgroundColor: `${COLORS.coral.DEFAULT}12`,
                }}
              >
                <ShieldCheck size={16} color={COLORS.coral.DEFAULT} strokeWidth={2} />
                <Text
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 12,
                    lineHeight: 17,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                  }}
                >
                  {t('listing.publish_needs_verification')}
                </Text>
              </View>
            ) : null}
          </Animated.View>
        ) : null}

        {/* Make */}
        <Animated.View collapsable={false} entering={enter(1)} testID="listing-details.make">
          <Select
            label={t('listing.make')}
            testID="listing-details.make-select"
            placeholder={t('listing.make_placeholder')}
            value={details.makeId ?? null}
            onChange={handleMakeChange}
            options={makeOptions}
            searchable
            sheetTitle={t('listing.make')}
            error={errors.makeId}
          />
        </Animated.View>

        {/* Model */}
        <Animated.View
          collapsable={false}
          entering={enter(2)}
          style={{ marginTop: SPACING.md }}
          testID="listing-details.model"
        >
          <Select
            label={t('listing.model')}
            testID="listing-details.model-select"
            placeholder={t('listing.model_placeholder')}
            value={details.modelId ?? null}
            onChange={handleModelChange}
            options={modelOptions}
            searchable
            sheetTitle={t('listing.model')}
            disabled={!details.makeId}
            error={errors.modelId}
          />
        </Animated.View>

        {/* Year */}
        <Animated.View
          collapsable={false}
          entering={enter(3)}
          style={{ marginTop: SPACING.md }}
          testID="listing-details.year"
        >
          <Select
            label={t('listing.year')}
            testID="listing-details.year-select"
            placeholder={t('listing.year_placeholder')}
            value={details.year != null ? String(details.year) : null}
            onChange={handleYearChange}
            options={yearOptions}
            sheetTitle={t('listing.year')}
            error={errors.year}
          />
          {/* Say the rule where it is discovered. The list simply stops at
                MIN_VEHICLE_YEAR, so a host with a 2008 pickup scrolls, does not
                find their car, and concludes the app is broken or that we do not
                want them. Interpolated from the constant so it cannot drift. */}
          {!errors.year ? (
            <Text
              style={{
                marginTop: SPACING.xs,
                fontSize: 12,
                lineHeight: 16,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
              }}
            >
              {t('listing.year_hint', { year: MIN_VEHICLE_YEAR })}
            </Text>
          ) : null}
        </Animated.View>

        {/* Category chips */}
        <Animated.View collapsable={false} entering={enter(4)} style={{ marginTop: SPACING.lg }}>
          <Text
            style={{
              fontSize: 13,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
              marginBottom: SPACING.sm,
              letterSpacing: 0.2,
            }}
          >
            {t('listing.category')}
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.sm }}>
            {CATEGORIES.map((cat) => (
              <Chip
                key={cat}
                label={t(`listing.categories.${cat}`)}
                selected={details.category === cat}
                onPress={() => {
                  setDetails({ category: cat })
                  setErrors((e) => ({ ...e, category: '' }))
                }}
                variant={details.category === cat ? 'filled' : 'outlined'}
                color={details.category === cat ? 'primary' : 'default'}
              />
            ))}
          </View>
          {errors.category ? (
            <Text
              style={{
                color: colors.textAccent,
                fontSize: 12,
                marginTop: SPACING.xs,
                fontFamily: FONTS.body,
              }}
            >
              {errors.category}
            </Text>
          ) : null}
        </Animated.View>

        {/* Color */}
        <Animated.View collapsable={false} entering={enter(5)} style={{ marginTop: SPACING.md }}>
          <TextInput
            label={t('listing.color')}
            placeholder={t('listing.color_placeholder')}
            value={colorText}
            onChangeText={(v) => {
              setColorText(v)
              setErrors((e) => ({ ...e, color: '' }))
            }}
            onBlur={() => {
              const normalized = colorText.trim() ? titleCase(colorText.trim()) : ''
              setColorText(normalized)
              setDetails({ color: normalized || undefined })
            }}
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect={false}
            importantForAutofill="no"
            spellCheck={false}
            error={errors.color}
            testID="listing-details.color-input"
          />
        </Animated.View>

        {/* Transmission chips */}
        <Animated.View collapsable={false} entering={enter(6)} style={{ marginTop: SPACING.lg }}>
          <Text
            style={{
              fontSize: 13,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
              marginBottom: SPACING.sm,
              letterSpacing: 0.2,
            }}
          >
            {t('listing.transmission')}
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.sm }}>
            {MARKET_TRANSMISSIONS.map((tr) => (
              <Chip
                key={tr}
                label={t(`listing.transmissions.${tr}`)}
                selected={toMarketTransmission(details.transmission) === tr}
                onPress={() => {
                  setDetails({ transmission: tr as TransmissionType })
                  setErrors((e) => ({ ...e, transmission: '' }))
                }}
                variant={toMarketTransmission(details.transmission) === tr ? 'filled' : 'outlined'}
                color={toMarketTransmission(details.transmission) === tr ? 'primary' : 'default'}
              />
            ))}
          </View>
          {errors.transmission ? (
            <Text
              style={{
                color: colors.textAccent,
                fontSize: 12,
                marginTop: SPACING.xs,
                fontFamily: FONTS.body,
              }}
            >
              {errors.transmission}
            </Text>
          ) : null}
        </Animated.View>

        {/* Fuel type chips */}
        <Animated.View collapsable={false} entering={enter(7)} style={{ marginTop: SPACING.lg }}>
          <Text
            style={{
              fontSize: 13,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
              marginBottom: SPACING.sm,
              letterSpacing: 0.2,
            }}
          >
            {t('listing.fuel_type')}
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.sm }}>
            {FUEL_TYPES.map((ft) => (
              <Chip
                key={ft}
                label={t(`listing.fuel_types.${ft}`)}
                selected={details.fuelType === ft}
                onPress={() => {
                  setDetails({ fuelType: ft })
                  setErrors((e) => ({ ...e, fuelType: '' }))
                }}
                variant={details.fuelType === ft ? 'filled' : 'outlined'}
                color={details.fuelType === ft ? 'primary' : 'default'}
              />
            ))}
          </View>
          {errors.fuelType ? (
            <Text
              style={{
                color: colors.textAccent,
                fontSize: 12,
                marginTop: SPACING.xs,
                fontFamily: FONTS.body,
              }}
            >
              {errors.fuelType}
            </Text>
          ) : null}
        </Animated.View>

        {/* Seats & Doors row */}
        <Animated.View
          collapsable={false}
          entering={enter(8)}
          style={{ marginTop: SPACING.md, flexDirection: 'row', gap: SPACING.md }}
        >
          {/* minWidth: 0 — without it a flex item will not shrink below its
                content, and "Puertas" pushed this field 32px off a 390px screen. */}
          <View style={{ flex: 1, minWidth: 0 }}>
            <TextInput
              label={t('listing.seats')}
              value={details.seats != null ? String(details.seats) : ''}
              onChangeText={(v) => {
                const n = parseInt(v, 10)
                if (!isNaN(n)) setDetails({ seats: n })
                else if (v === '') setDetails({ seats: undefined })
              }}
              keyboardType="number-pad"
              error={errors.seats}
              testID="listing-details.seats-input"
            />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <TextInput
              label={t('listing.doors')}
              value={details.doors != null ? String(details.doors) : ''}
              onChangeText={(v) => {
                const n = parseInt(v, 10)
                if (!isNaN(n)) setDetails({ doors: n })
                else if (v === '') setDetails({ doors: undefined })
              }}
              keyboardType="number-pad"
              error={errors.doors}
              testID="listing-details.doors-input"
            />
          </View>
        </Animated.View>

        {/* Plate */}
        <Animated.View collapsable={false} entering={enter(9)} style={{ marginTop: SPACING.md }}>
          <TextInput
            label={t('listing.plate_number')}
            placeholder={t('listing.plate_placeholder')}
            value={details.plateNumber ?? ''}
            onChangeText={(v) => {
              setDetails({ plateNumber: normalizePlate(v) })
              setErrors((e) => ({ ...e, plateNumber: '' }))
            }}
            autoCapitalize="characters"
            autoCorrect={false}
            error={errors.plateNumber}
            testID="listing-details.plate-input"
          />
        </Animated.View>
      </KeyboardAwareView>

      <FormActionBar style={{ backgroundColor: colors.background }}>
        <Button
          onPress={handleNext}
          variant="primary"
          size="lg"
          title={continueLabel}
          testID="listing-details.next-button"
        />
      </FormActionBar>
    </ScreenContainer>
  )
}
